import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Truck, Plus, Search, Filter, RefreshCw, Eye, Play, CheckCircle2,
  Clock, Loader2, Package, MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShipmentList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [shipments, setShipments] = useState([]);
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadShipments();
  }, []);


  const loadShipments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/warehouse/shipments`);
      setShipments(response.data);
    } catch (error) {
      console.error('Error loading shipments:', error);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const filterShipments = useCallback(() => {
    let filtered = [...shipments];
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.shipment_number?.toLowerCase().includes(term) ||
        s.carrier_name?.toLowerCase().includes(term) ||
        s.tracking_number?.toLowerCase().includes(term)
      );
    }
    setFilteredShipments(filtered);
  }, [shipments, searchTerm, statusFilter]);

  useEffect(() => {
    filterShipments();
  }, [filterShipments]);

  const getStatusColor = (status) => {
    const colors = {
      'PLANNED': 'bg-orange-100 text-orange-800',
      'IN_TRANSIT': 'bg-orange-100 text-orange-800',
      'DELIVERED': 'bg-green-100 text-green-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PLANNED': return <Clock className="w-4 h-4" />;
      case 'IN_TRANSIT': return <Truck className="w-4 h-4" />;
      case 'DELIVERED': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const handleStartShipment = async (shipmentNumber) => {
    try {
      await axios.post(`${BACKEND_URL}/api/warehouse/shipments/${shipmentNumber}/start?started_by=DRIVER`);
      toast.success('Shipment started');
      loadShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start shipment');
    }
  };

  const handleCompleteShipment = async (shipmentNumber) => {
    try {
      await axios.post(`${BACKEND_URL}/api/warehouse/shipments/${shipmentNumber}/complete?completed_by=DRIVER`);
      toast.success('Shipment completed');
      loadShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete shipment');
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="shipment-list">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-7 h-7 text-orange-600" />
              Shipments
            </h1>
            <p className="text-sm text-gray-500">Manage outbound shipments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/warehouse/pick-lists')}>
              <Package className="w-4 h-4 mr-2" />
              Pick Lists
            </Button>
            {canWrite('warehouse_shipping') && (
            <Button onClick={() => navigate('/warehouse/shipments/create')} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Shipment
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by shipment, carrier, tracking..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="PLANNED">Planned</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="DELIVERED">Delivered</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadShipments}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading shipments...</p>
            </div>
          ) : filteredShipments.length === 0 ? (
            <div className="p-8 text-center">
              <Truck className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No shipments found</p>
              {canWrite('warehouse_shipping') && (
              <Button onClick={() => navigate('/warehouse/shipments/create')} className="mt-4" variant="outline">
                Create Shipment
              </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipment #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carrier</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Deliveries</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">HUs</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Planned Departure</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tracking</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredShipments.map((ship) => (
                    <tr key={ship.shipment_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-orange-600">{ship.shipment_number}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ship.status)}`}>
                          {getStatusIcon(ship.status)}
                          {ship.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{ship.carrier_name || '-'}</p>
                        <p className="text-xs text-gray-500">{ship.vehicle_number}</p>
                      </td>
                      <td className="px-4 py-3 text-center">{ship.total_deliveries}</td>
                      <td className="px-4 py-3 text-center">{ship.total_handling_units}</td>
                      <td className="px-4 py-3 text-gray-600">{ship.planned_departure}</td>
                      <td className="px-4 py-3 text-gray-600">{ship.tracking_number || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/warehouse/shipments/${ship.shipment_number}`)}
                            className="text-orange-600"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {ship.status === 'PLANNED' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStartShipment(ship.shipment_number)}
                              className="text-orange-600"
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          {ship.status === 'IN_TRANSIT' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCompleteShipment(ship.shipment_number)}
                              className="text-green-600"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredShipments.length} of {shipments.length} shipments
        </div>
      </div>
    </Layout>
  );
};

export default ShipmentList;
